<template>
    <v-container>
        <v-card v-show="!this.$route.query.transactionHashes" :loading="progress">
            <v-card-title class="font-weight-light text-h5">
                Commission a BraNFT™
            </v-card-title>
            <v-card-subtitle>Commission a BraNFT™ from an individual who is not yet a Creator.</v-card-subtitle>

            <v-card-text class="text-body-2">
                <p :class="{ 'description': description }" class="my-8"><span :class="{ 'red--text': error && error.code === 401 }">Registered users can add a photo when commissioning new art.</span> A benefit to being the first commissioner is that you get to choose the users profile photo. Later we will implement a vote system for image changes.</p>
                <p :class="{ 'description': description }" class="my-8">And finally, of course the verified social account user always has the power to change the image.</p> 
                <v-form v-model="valid" ref="form">
                    <v-row>
                        <v-col :cols="$vuetify.breakpoint.mobile ? 4 : 3">
                        </v-col>
                        <v-col :cols="$vuetify.breakpoint.mobile ? 8 : 9">
                            <v-btn v-show="error" class="mr-2" outlined @click="signupButtonHandler({ role_hint: 'collector' })">Sign In</v-btn>
                            <v-subheader class="text-caption">Picture</v-subheader>
                            <v-img v-show="socialNetworkProfilePhoto" contain :src="socialNetworkProfilePhoto && socialNetworkProfilePhoto.https"></v-img>
                            <v-container v-show="!socialNetworkProfilePhoto">
                                <v-sheet elevation="1" class="rounded-lg">
                                    <file-pond class="image" ref="filepond_social_profile_photo" name="filepond_social_profile_photo"
                                               accepted-file-types="image/*"
                                               stylePanelLayout="integrated"
                                               @error="err => error = err"
                                               :server="server"
                                               :credits="null"></file-pond>
                                </v-sheet>
                            </v-container>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col :cols="$vuetify.breakpoint.mobile ? 4 : 3">
                            <v-select id="network" label="network" v-model="network" @input="verify" :items="items" :rules="[v => !!v || 'Social network is required']"></v-select>
                        </v-col>
                        <v-col>
                            <v-text-field ref="username" id="username" v-model="username" :prefix="prefix" label="username" @input="verify" :hint="hint" :persistent-hint="true" :rules="[v => !!v || 'Social username is required']">
                                <template v-slot:message>
                                    Confirm by opening user profile <a @click="usernameConfirmed = true" rel="noopener" target="_blank" :href="hint">{{ hint }}</a>
                                </template>
                            </v-text-field>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-spacer></v-spacer>
                        <v-col v-show="usernameConfirmed || NODE_ENV !== 'production'" cols="9">
                            <bnft-price-input id="bid" ref="bid" label="bid"></bnft-price-input>
                        </v-col>
                    </v-row>
                </v-form>
            </v-card-text>

            <v-divider></v-divider>

            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn :disabled="!valid"
                       color="primary"
                       text
                       @click="submit">
                    Commission
                </v-btn>
            </v-card-actions>
        </v-card>
        <v-card v-show="this.$route.query.transactionHashes" :loading="progress">
            <v-card-title class="font-weight-light text-lg-h5 text-h6 justify-center">
                Congratulations! Your commission bid for <span class="text-capitalize mx-1">{{ network }}</span> user {{ username }} has been accepted.
            </v-card-title>

            <v-img class="rounded-lg mx-2" max-height="800px" v-show="socialNetworkProfilePhoto" contain :src="socialNetworkProfilePhoto && socialNetworkProfilePhoto.https"></v-img>
            <v-card-text class="body-2">
                <bnft-stats-counter class="mb-4"
                                    :bids="commissionStats.bids.length"
                                    :averageBid="`${commissionStats.averageBid}` | formatNearAmount"
                                    :totalBidAmount="`${commissionStats.totalBidAmount}` | formatNearAmount">
                </bnft-stats-counter>
                Your blockchain transaction can be found here: <span v-html="$near.explorerTransactionLink($route)"></span>.<br />
                You can also check on the commission status from the <router-link class="font-italic" style="text-decoration: none" :to="{ name: 'commissions', params: { network, username }}">commissions</router-link> page.
            </v-card-text>
            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn
                       color="primary"
                       text
                       @click="$emit('close')">
                    Okay
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-container>
</template>
<style scoped>
</style>
<script>
import App from "../App.vue";
import BnftPriceInput from "./BnftPriceInput.vue";
import BnftStatsCounter from "./BnftStatsCounter.vue";
import { utils } from "near-api-js";
import vueFilePond from "vue-filepond";
import FilePondPluginFileValidateType from "filepond-plugin-file-validate-type";
import FilePondPluginImagePreview from "filepond-plugin-image-preview";
import FilePondPluginFilePoster from "filepond-plugin-file-poster";
import "filepond/dist/filepond.min.css";
import "filepond-plugin-image-preview/dist/filepond-plugin-image-preview.min.css";
import "filepond-plugin-file-poster/dist/filepond-plugin-file-poster.css";

const FilePond = vueFilePond(
    FilePondPluginFileValidateType,
    FilePondPluginImagePreview,
    FilePondPluginFilePoster
);

export default {
    name: "RequestUserCard",
    extends: App,
    components: {
        BnftPriceInput,
        BnftStatsCounter,
        FilePond,
    },
    props: {
        transactionHashes: String,
        initUsername: String,
        initNetwork: String,
    },
    methods: {
        verify() {
            const { prefix, network, username } = this;
            this.getProfilePhoto();
            this.$set(this, "network", network);
            this.$set(this, "username", username);
            this.$set(
                this,
                "hint",
                network && username
                    ? `https://${network}.com/${prefix}${username}`
                    : undefined
            );
        },
        async submit() {
            this.progress = true;
            try {
                const formInputs = this.$refs.form.inputs
                    .filter((input) => input.id !== "currency")
                    .reduce((acc, cur) => {
                        acc = {
                            [cur.id]: cur.value,
                            ...acc,
                        };
                        return acc;
                    }, {});
                const query = new URLSearchParams({
                    state: "commission_bid",
                    ...this.$route.params,
                }).toString();
                const params = {
                    walletSigninArgs: {
                        successCallback: `${this.$route.path}?${query}`,
                    },
                    socialNetworkProfilePhoto: this.socialNetworkProfilePhoto,
                    ...formInputs,
                };
                if (!this.socialNetworkProfilePhoto) {
                    const data = await this.$backend.socialNetworkProfilePhoto(
                        formInputs.network,
                        formInputs.username,
                        this.uploadId
                    );
                    if (data) {
                        this.socialNetworkProfilePhoto = data.photo;
                    }
                }

                await this.$near.commission(params);
                this.progress = false;
            } catch (error) {
                this.progress = false;
                if (
                    !error.message.match(
                        /Parse error: Failed parsing args: missing field `account_id`/i
                    )
                ) {
                    this.$root.$emit("commissionError", error);
                }
            }
        },
        async getProfilePhoto() {
            if (this.network && this.username) {
                const data = await this.$backend.socialNetworkProfilePhoto(
                    this.network,
                    this.username
                );
                if (data instanceof Error) this.socialNetworkProfilePhoto = undefined;
                if (data && data.photo && !data.photo.placeholder) this.socialNetworkProfilePhoto = data.photo;
            }
        },
    },
    filters: {
        formatNearAmount(value) {
            const formatted = utils.format.formatNearAmount(value, 2);
            return parseFloat(formatted);
        },
    },
    computed: {
        prefix() {
            return this.network === "tiktok" ? "@" : "";
        },
        username: {
            get: function () {
                return (
                    this.$route.query.commission ||
                    this.$store.state.commissionUsername
                );
            },
            set: function (value) {
                this.$store.commit("set", {
                    ["commissionUsername"]: value.trim(),
                });
            },
        },
        network: {
            get: function () {
                return this.$store.state.commissionNetwork;
            },
            set: function (value) {
                this.$store.commit("set", { ["commissionNetwork"]: value });
            },
        },
        commissionStats() {
            const stats = this.commissions.reduce(
                (acc, cur) => {
                    acc.bids.push(cur);
                    acc.totalBidAmount =
                        BigInt(acc.totalBidAmount) + BigInt(cur.price);
                    acc.averageBid = BigInt(
                        acc.totalBidAmount / BigInt(acc.bids.length)
                    );
                    return acc;
                },
                {
                    bids: [],
                    totalBidAmount: 0,
                    averageBid: 0,
                }
            );
            return stats;
        },
        server() {
            return {
                url: `${process.env.VUE_APP_API_SERVER}/v1/social/upload/process`,
                withCredentials: true,
                instantUpload: true,
                headers: {
                    Authorization: `Bearer ${this.token}`,
                },
            };
        },
        uploadId() {
            return this.$refs.form.$el.querySelector('input[type="hidden"]')
                ? this.$refs.form.$el.querySelector('input[type="hidden"]')
                      .value
                : undefined;
        },
    },
    data: function () {
        return {
            items: ["tiktok", "facebook", "instagram", "twitter"],
            usernameConfirmed: false,
            verified: null,
            valid: null,
            progress: false,
            commissions: [],
            hint: undefined,
            socialNetworkProfilePhoto: undefined,
            token: null,
            description: true,
            error: false,
        };
    },
    mounted() {
        const self = this;
        self.$auth.$on('authenticated', async () => {
            if (!self.error) return;
            self.error = undefined;
            self.token = await self.$auth.getTokenSilently();
            self.$refs.filepond_social_profile_photo.processFile();
        });
        if (self.initUsername) self.username = self.initUsername;
        if (self.initNetwork) self.network = self.initNetwork;
        const { network, username } = this;
        if (this.$route.query.transactionHashes) {
            (async () => {
                self.commissions = await self.$near.commissionBidsForUser({
                    network,
                    username,
                });
            })();
        }
        (async () => {
            self.token = await self.$auth.getTokenSilently();
            self.getProfilePhoto();
        })();
        if (self.username && self.network) self.verify();
    },
};
</script>
